<template>
  <v-navigation-drawer
    v-model="localValue"
    fixed
    right
    temporary
    width="700"
    class="charger-drawer"
    style="z-index: 210"
  >
    <!-- Header -->
    <div class="charger-drawer-header">
      <div class="charger-drawer-header-top">
        <div class="charger-drawer-header-info">
          <div class="charger-drawer-name-row">
            <span v-if="charger" class="charger-name">{{ charger.name || charger.identifier }}</span>
            <v-chip
              v-if="charger"
              x-small
              :color="charger.connected ? '#10B981' : '#EF4444'"
              text-color="white"
              class="ml-2"
            >
              {{ charger.connected ? $t('org-dashboard.charger-connected') : $t('org-dashboard.charger-disconnected') }}
            </v-chip>
          </div>
          <div v-if="charger" class="charger-drawer-meta">
            <span v-if="charger.identifier && charger.identifier !== charger.name" class="charger-meta-item">
              <v-icon x-small color="#9ca3af" class="mr-1">mdi-identifier</v-icon>{{ charger.identifier }}
            </span>
            <span v-if="charger.evseId || charger.ocppId" class="charger-meta-item">
              <v-icon x-small color="#9ca3af" class="mr-1">mdi-tag-outline</v-icon>{{ charger.evseId || charger.ocppId }}
            </span>
            <span v-if="charger.brand || charger.model" class="charger-meta-item">
              <v-icon x-small color="#9ca3af" class="mr-1">mdi-ev-plug-type2</v-icon>{{ [charger.brand, charger.model].filter(Boolean).join(' ') }}
            </span>
            <span v-if="charger.address || charger.location" class="charger-meta-item">
              <v-icon x-small color="#9ca3af" class="mr-1">mdi-map-marker-outline</v-icon>{{ formatAddress(charger.address || charger.location) }}
            </span>
            <a v-if="charger.memberId" class="charger-meta-item charger-meta-link" :href="memberDetailHref" target="_blank">
              <v-icon x-small color="#1976d2" class="mr-1">mdi-account</v-icon>{{ charger.memberName || charger.memberId }}
            </a>
          </div>
        </div>
        <div class="charger-drawer-header-actions">
          <v-btn v-if="charger" icon small :href="chargerDetailHref" target="_blank" :title="$t('org-dashboard.charger-drawer-open-tab')">
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn icon small @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="charger-drawer-loading">
      <v-progress-circular indeterminate color="#FF5722" size="32" />
    </div>

    <!-- Content -->
    <div v-else-if="data" class="charger-drawer-content">
      <!-- KPIs -->
      <div class="drawer-section">
        <div class="drawer-kpi-grid">
          <div v-for="(kpi, i) in kpiItems" :key="i" class="drawer-kpi-card">
            <div class="drawer-kpi-value">{{ kpi.value }}</div>
            <div class="drawer-kpi-label">{{ kpi.label }}</div>
          </div>
        </div>
      </div>

      <!-- Heatmap -->
      <div class="drawer-section">
        <h4 class="drawer-section-title">{{ $t('org-dashboard.charger-heatmap') }}</h4>
        <div class="drawer-heatmap">
          <div class="heatmap-row" v-for="(row, ri) in heatmapRows" :key="ri">
            <div class="heatmap-day">{{ row.day }}</div>
            <div class="heatmap-cells">
              <div
                v-for="(cell, ci) in row.hours"
                :key="ci"
                class="heatmap-cell"
                :style="{ backgroundColor: getHeatColor(cell) }"
                :title="`${row.day} ${ci}h: ${cell.sessions || 0}`"
              />
            </div>
          </div>
          <div class="heatmap-hours-row">
            <div class="heatmap-day" />
            <div class="heatmap-cells">
              <span v-for="h in [0, 6, 12, 18, 23]" :key="h" class="heatmap-hour-label" :style="{ gridColumn: h + 1 }">{{ h }}h</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Trends Chart -->
      <div class="drawer-section" v-if="data.trends && data.trends.length">
        <div class="drawer-section-header">
          <h4 class="drawer-section-title">{{ $t('org-dashboard.charger-trends') }}</h4>
          <v-btn-toggle v-model="trendMetric" mandatory dense class="trend-toggle">
            <v-btn x-small value="sessions">{{ $t('org-dashboard.sessions-short') }}</v-btn>
            <v-btn x-small value="kwh">kWh</v-btn>
          </v-btn-toggle>
        </div>
        <highcharts v-if="trendChartOptions" :options="trendChartOptions" />
      </div>

      <!-- Events -->
      <div class="drawer-section">
        <h4 class="drawer-section-title">
          {{ $t('org-dashboard.charger-events') }}
          <v-chip v-if="errorsCount > 0" x-small color="#EF4444" text-color="white" class="ml-2">
            {{ $tc('org-dashboard.charger-events-count', errorsCount, { count: errorsCount }) }}
          </v-chip>
        </h4>
        <div v-if="eventsList.length === 0" class="drawer-empty-text">
          {{ $t('org-dashboard.charger-no-events') }}
        </div>
        <div v-else class="drawer-events-list">
          <div v-for="(evt, i) in eventsList" :key="i" class="drawer-event-row">
            <v-chip x-small outlined :color="getStatusColor(evt.status)" class="drawer-event-chip">{{ evt.status }}</v-chip>
            <span class="drawer-event-evse">{{ evt.evsePublicId || evt.evseId }}</span>
            <span class="drawer-event-time">{{ formatEventDate(evt.timestamp) }}</span>
          </div>
        </div>
      </div>

      <!-- Hardware -->
      <div class="drawer-section" v-if="charger && charger.evses && charger.evses.length">
        <h4 class="drawer-section-title">{{ $t('org-dashboard.charger-hardware') }}</h4>
        <div v-for="(evse, ei) in charger.evses" :key="ei" class="drawer-evse-block">
          <div class="drawer-evse-header">
            <span class="drawer-evse-id">{{ $t('org-dashboard.charger-evse') }} {{ evse.evseId || evse.publicId }}</span>
            <v-chip x-small outlined :color="getStatusColor(evse.status)">{{ evse.status }}</v-chip>
          </div>
          <div v-for="(conn, ci) in evse.connectors" :key="ci" class="drawer-connector-row">
            <span class="drawer-connector-type">{{ conn.type || conn.standard }}</span>
            <span class="drawer-connector-power">{{ $t('org-dashboard.charger-power') }}: {{ formatPower(conn.power) }}</span>
            <span class="drawer-connector-format">{{ $t('org-dashboard.charger-format') }}: {{ conn.format }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="charger-drawer-empty">
      <v-icon large color="grey lighten-1">mdi-information-outline</v-icon>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { Chart } from 'highcharts-vue'

const STATUS_COLORS = {
  AVAILABLE: '#00897E',
  CHARGING: '#DEAC3C',
  OFFLINE: '#E53C22',
  BLOCKED: '#b3028d',
  OUTOFORDER: '#758093',
  UNKNOWN: '#9e9e9e',
  PLANNED: '#1586e3',
  MAINTENANCE: '#6277a1',
}

const HEATMAP_COLORS = ['#FFF7ED', '#FDBA74', '#FB923C', '#EA580C', '#B91C1C']

export default {
  name: 'DashboardChargerDrawer',
  components: {
    highcharts: Chart,
  },
  props: {
    value: { type: Boolean, default: false },
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    organizationId: { type: String, default: '' },
  },

  data() {
    return {
      trendMetric: 'sessions',
    }
  },

  computed: {
    localValue: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },

    charger() {
      return this.data?.charger || null
    },

    chargerDetailHref() {
      if (!this.charger?.chargingPointId) return '#'
      const route = this.$router.resolve({ name: 'charging-points.detail', params: { id: this.charger.chargingPointId } })
      return route.href
    },

    memberDetailHref() {
      if (!this.charger?.memberId) return '#'
      const route = this.$router.resolve({ name: 'member', params: { memberId: this.charger.memberId } })
      return route.href
    },

    kpiItems() {
      const kpis = this.data?.kpis
      if (!kpis) return []
      return [
        { label: this.$t('org-dashboard.charger-kpis-sessions'), value: kpis.sessions != null ? kpis.sessions.toLocaleString() : '-' },
        { label: this.$t('org-dashboard.charger-kpis-energy'), value: kpis.energy != null ? Math.round(kpis.energy).toLocaleString() + ' kWh' : '-' },
        { label: this.$t('org-dashboard.charger-kpis-occupancy'), value: kpis.occupancy != null ? Math.round(kpis.occupancy) + '%' : '-' },
        { label: this.$t('org-dashboard.charger-kpis-avg-duration'), value: this.formatDurationSeconds(kpis.avgDuration) },
        { label: this.$t('org-dashboard.charger-kpis-revenue'), value: kpis.revenue != null ? this.formatCurrency(kpis.revenue) : '-' },
        { label: this.$t('org-dashboard.charger-kpis-last-activity'), value: kpis.lastActivity ? this.formatShortDate(kpis.lastActivity) : '-' },
      ]
    },

    heatmapRows() {
      const raw = this.data?.heatmap
      if (!raw?.length) return []

      const dayKeys = ['day-mon', 'day-tue', 'day-wed', 'day-thu', 'day-fri', 'day-sat', 'day-sun']
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60)

      // Group by day, fill 7x24 (shift hours from GMT to local)
      const grouped = {}
      raw.forEach(entry => {
        let hour = entry.hour + tzOffset
        let day = entry.day
        if (hour >= 24) { hour -= 24; day = day < 7 ? day + 1 : 1 }
        else if (hour < 0) { hour += 24; day = day > 1 ? day - 1 : 7 }
        if (!grouped[day]) grouped[day] = new Array(24).fill(null).map(() => ({ sessions: 0 }))
        if (hour >= 0 && hour < 24) {
          grouped[day][hour] = { sessions: entry.sessions || 0 }
        }
      })

      return [1, 2, 3, 4, 5, 6, 7].map(d => ({
        day: this.$t('org-dashboard.' + dayKeys[d - 1]),
        hours: grouped[d] || new Array(24).fill(null).map(() => ({ sessions: 0 })),
      }))
    },

    heatmapMax() {
      const raw = this.data?.heatmap
      if (!raw?.length) return 1
      return Math.max(...raw.map(r => r.sessions || 0), 1)
    },

    errorsCount() {
      if (!this.data || !this.data.events) return 0
      return this.data.events.errorsCount || 0
    },

    eventsList() {
      const eventsObj = this.data ? this.data.events : null
      if (!eventsObj) return []
      // API structure: { errorsCount, errorHeatmap, events: [...] }
      const list = Array.isArray(eventsObj) ? eventsObj : (Array.isArray(eventsObj.events) ? eventsObj.events : [])
      return list.slice(0, 10).map(evt => {
        let evsePublicId = evt.evseId
        if (this.charger && this.charger.evses) {
          const evse = this.charger.evses.find(e => e.id === evt.evseId || e.evseId === evt.evseId)
          if (evse) evsePublicId = evse.publicId || evse.evseId || evt.evseId
        }
        return {
          status: evt.status,
          evseId: evt.evseId,
          evsePublicId: evsePublicId,
          timestamp: evt.createdAt || evt.timestamp || evt.date,
        }
      })
    },

    trendChartOptions() {
      const trends = this.data?.trends
      if (!trends?.length) return null

      const isKwh = this.trendMetric === 'kwh'
      const metricSeries = isKwh
        ? {
            name: this.$t('org-dashboard.charger-kpis-energy') + ' (kWh)',
            type: 'spline',
            color: '#F59E0B',
            yAxis: 0,
            tooltip: { valueSuffix: ' kWh' },
            data: trends.map(t => [new Date(t.date).getTime(), Math.round(t.energy || 0)]),
          }
        : {
            name: this.$t('org-dashboard.charger-kpis-sessions'),
            type: 'spline',
            color: '#3B82F6',
            yAxis: 0,
            data: trends.map(t => [new Date(t.date).getTime(), t.sessions || 0]),
          }

      return {
        chart: {
          height: 220,
          backgroundColor: 'transparent',
          style: { fontFamily: 'inherit' },
          spacingTop: 10,
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: true, itemStyle: { fontSize: '10px', fontWeight: '500' } },
        xAxis: {
          type: 'datetime',
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          gridLineWidth: 0,
          lineColor: '#e5e7eb',
        },
        yAxis: [
          {
            title: { text: null },
            labels: { style: { fontSize: '10px', color: '#6b7280' } },
            gridLineColor: '#f1f5f9',
            min: 0,
          },
          {
            title: { text: null },
            labels: { style: { fontSize: '10px', color: '#6b7280' }, format: '{value}%' },
            opposite: true,
            gridLineColor: 'transparent',
            min: 0,
            max: 100,
          },
        ],
        tooltip: {
          shared: true,
          crosshairs: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          style: { fontSize: '11px' },
        },
        plotOptions: {
          spline: { lineWidth: 2, marker: { enabled: false } },
          areaspline: { fillOpacity: 0.12, lineWidth: 1.5, marker: { enabled: false } },
          series: { animation: { duration: 600 } },
        },
        series: [
          {
            name: this.$t('org-dashboard.charger-kpis-occupancy') + ' (%)',
            type: 'areaspline',
            color: '#10B981',
            yAxis: 1,
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [[0, 'rgba(16, 185, 129, 0.2)'], [1, 'rgba(16, 185, 129, 0.02)']],
            },
            tooltip: { valueSuffix: ' %' },
            data: trends.map(t => [new Date(t.date).getTime(), Math.round((t.occupancyRate ?? t.occupancy ?? 0) * 10) / 10]),
          },
          metricSeries,
        ],
      }
    },
  },

  methods: {
    close() {
      this.localValue = false
    },

    getStatusColor(status) {
      return STATUS_COLORS[(status || '').toUpperCase()] || '#9e9e9e'
    },

    getHeatColor(cell) {
      const val = (cell.sessions || 0) / this.heatmapMax
      if (val === 0) return HEATMAP_COLORS[0]
      const idx = Math.min(Math.floor(val * (HEATMAP_COLORS.length - 1)) + 1, HEATMAP_COLORS.length - 1)
      return HEATMAP_COLORS[idx]
    },

    formatDurationSeconds(seconds) {
      if (seconds == null) return '-'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      if (h > 0) return `${h}h ${m}m`
      return `${m}m`
    },

    formatAddress(addr) {
      if (!addr) return '-'
      if (typeof addr === 'string') return addr
      if (typeof addr === 'object') {
        const parts = [
          addr.number ? addr.number : null,
          addr.street,
          addr.zipcode,
          addr.city,
          addr.country,
        ].filter(Boolean)
        return parts.join(' ') || '-'
      }
      return String(addr)
    },

    formatCurrency(val) {
      if (val == null) return '-'
      return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: 'EUR' }).format(val)
    },

    formatShortDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString(this.$i18n.locale, { day: '2-digit', month: 'short' })
    },

    formatEventDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString(this.$i18n.locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    },

    formatPower(watts) {
      if (watts == null) return '-'
      if (watts >= 1000) return (watts / 1000).toFixed(1) + ' kW'
      return watts + ' W'
    },
  },
}
</script>

<style lang="scss" scoped>
.charger-drawer {
  ::v-deep .v-navigation-drawer__content {
    overflow-y: auto;
  }
}

.charger-drawer-header {
  border-bottom: 1px solid #e5e7eb;
  padding: 14px 16px;
}

.charger-drawer-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.charger-drawer-header-info {
  flex: 1;
  min-width: 0;
}

.charger-drawer-name-row {
  display: flex;
  align-items: center;
}

.charger-name {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.charger-drawer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 6px;
}

.charger-meta-item {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: #6b7280;
}

.charger-meta-link {
  color: #1976d2;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.charger-drawer-header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.charger-drawer-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

.charger-drawer-content {
  padding: 16px 20px;
}

.charger-drawer-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

.drawer-section {
  margin-bottom: 24px;
}

.drawer-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  .drawer-section-title {
    margin-bottom: 0;
  }
}

.trend-toggle {
  &.v-btn-toggle {
    border-radius: 4px;
    background: #f3f4f6;

    .v-btn {
      height: 24px !important;
      min-width: 0 !important;
      padding: 0 10px !important;
      font-size: 11px !important;
      font-weight: 600;
      letter-spacing: 0;
      text-transform: none;
      border: none !important;

      &.v-btn--active {
        background: #fff !important;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        color: #FF5722 !important;
      }
    }
  }
}

.drawer-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.drawer-empty-text {
  font-size: 12px;
  color: #9ca3af;
  padding: 12px 0;
}

/* KPIs */
.drawer-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.drawer-kpi-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #f3f4f6;
  text-align: center;
}

.drawer-kpi-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
}

.drawer-kpi-label {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

/* Heatmap */
.drawer-heatmap {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
}

.heatmap-row {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}

.heatmap-day {
  width: 32px;
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  flex-shrink: 0;
}

.heatmap-cells {
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  gap: 1px;
  flex: 1;
}

.heatmap-cell {
  aspect-ratio: 1;
  border-radius: 2px;
  min-height: 12px;
}

.heatmap-hours-row {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.heatmap-hours-row .heatmap-cells {
  position: relative;
}

.heatmap-hour-label {
  font-size: 9px;
  color: #9ca3af;
}

/* Events */
.drawer-events-list {
  max-height: 300px;
  overflow-y: auto;
}

.drawer-event-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 12px;

  &:last-child {
    border-bottom: none;
  }
}

.drawer-event-chip {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.drawer-event-evse {
  color: #4b5563;
  font-weight: 500;
  flex: 1;
}

.drawer-event-time {
  color: #9ca3af;
  font-size: 11px;
  flex-shrink: 0;
}

/* Hardware */
.drawer-evse-block {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  border: 1px solid #f3f4f6;
}

.drawer-evse-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.drawer-evse-id {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.drawer-connector-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 0 4px 12px;
  font-size: 11px;
  color: #6b7280;
}

.drawer-connector-type {
  font-weight: 600;
  color: #4b5563;
}
</style>
